/* eslint-disable react/prop-types */
import classNames from 'classnames';
import React, { useCallback } from 'react';
import { Form } from 'react-bootstrap';
import { useTable, usePagination, useRowSelect } from 'react-table';
import y from 'y4/Utils';

export const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, className, ...rest }, ref) => {
    const defaultRef = React.useRef();

    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <Form.Check
        type="checkbox"
        className={classNames('form-check fs-0 mb-0', className)}
      >
        <Form.Check.Input type="checkbox" ref={resolvedRef} {...rest} />
      </Form.Check>
    );
  }
);

const PagingAdvanceTableWrapper = ({
  cacheId,
  children,
  columns,
  data,
  selection,
  selectionColumnWidth,
  selectionChanged,
  pagination,
  fetchData,
  pages,
  selectedIds = {},
  perPage = 10,
  atPage = 0
}) => {
  //idUi or default
  const getRowId = useCallback(
    (row, relativeIndex, parent) => {
      if (row.idUi) return row.idUi;
      if (row.id) return row.id;
      return parent ? [parent.id, relativeIndex].join('.') : relativeIndex;
    },
    [columns, data]
  );

  //Init info from import info
  let lastSelectedIds = selectedIds;
  let lastPageSize = perPage;
  let lastPageIndex = atPage;

  //Read state from session
  if (cacheId) {
    const state_json = y.getSessionItem(`${cacheId}_atw`);
    if (state_json) {
      const state = JSON.parse(state_json);
      if (state) {
        lastSelectedIds = state.selectedRowIds;
        lastPageSize = state.pageSize;
        lastPageIndex = state.pageIndex;
      }
    }
  }

  let selected_counter = 0;

  const {
    getTableProps,
    headers,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    gotoPage,
    selectedFlatRows,
    pageCount,
    state: { pageIndex, pageSize, selectedRowIds }
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize: pagination ? lastPageSize : data.length,
        pageIndex: lastPageIndex,
        selectedRowIds: lastSelectedIds ?? {}
      },
      getRowId,
      manualPagination: true, // Tell the usePagination hook that we'll handle our own data fetching  This means we'll also have to provide our own pageCount.
      pageCount: pages
    },
    usePagination,
    useRowSelect,
    hooks => {
      if (selection) {
        hooks.visibleColumns.push(columns => [
          {
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            headerProps: {
              style: {
                width: selectionColumnWidth
              }
            },
            cellProps: {
              style: {
                width: selectionColumnWidth
              }
            },
            Cell: ({ row }) => {
              const trsps = { ...row.getToggleRowSelectedProps() };
              trsps.onChange = () => {
                row.toggleRowSelected(!row.isSelected);
                row.original['SELECTED_ORDER_INDEX'] = ++selected_counter;
              };
              return (
                <div>
                  <IndeterminateCheckbox {...trsps} />
                </div>
              );
            }
          },
          ...columns
        ]);
      }
    }
  );

  if (selection) {
    React.useEffect(() => {
      if (selectionChanged)
        selectionChanged(selectedFlatRows.map(d => d.original));
    }, [selectedFlatRows]);
  }

  if (cacheId) {
    React.useEffect(() => {
      y.setSessionItem(
        `${cacheId}_atw`,
        JSON.stringify({
          pageIndex,
          pageSize,
          selectedRowIds
        })
      );
    }, [pageIndex, pageSize, selectedRowIds]);
  }

  // Listen for changes in pagination and use the state to fetch our new data
  React.useEffect(() => {
    fetchData({ pageIndex, pageSize });
  }, [fetchData, pageIndex, pageSize]);

  // If the page changes, we want to go to that page
  React.useEffect(() => {
    gotoPage(atPage);
  }, [atPage]);

  const recursiveMap = children => {
    return React.Children.map(children, child => {
      if (child.props?.children) {
        return React.cloneElement(child, {
          children: recursiveMap(child.props.children)
        });
      } else {
        if (child.props?.table) {
          return React.cloneElement(child, {
            ...child.props,
            getTableProps,
            headers,
            page,
            prepareRow,
            canPreviousPage,
            canNextPage,
            nextPage,
            previousPage,
            gotoPage,
            pageCount,
            pageIndex,
            selectedRowIds,
            pageSize,
            setPageSize
          });
        } else {
          return child;
        }
      }
    });
  };

  return <>{recursiveMap(children)}</>;
};

export default PagingAdvanceTableWrapper;
